"use client";

import Link from "next/link";
import { Building2, CirclePlus, Briefcase, UserPlus, Settings, Radio } from "lucide-react";

import { Card, CardContent } from "@components/ui/card";
import { usePermissions } from "@hooks/use-permissions";
import { Permission } from "@lib/auth/scopes";
import { cn } from "@lib/utils";

const iconMap = {
    building2: Building2,
    circlePlus: CirclePlus,
    briefcase: Briefcase,
    userPlus: UserPlus,
    radio: Radio,
    settings: Settings,
} as const;

type IconName = keyof typeof iconMap;

interface QuickActionTileProps {
    title: string;
    description: string;
    href: string;
    iconName: IconName;
    permissions: Permission[];
    className?: string;
}

export function QuickActionTile({ title, description, href, iconName, permissions, className }: Readonly<QuickActionTileProps>) {
    const Icon = iconMap[iconName];
    const hasPermission = usePermissions(...permissions);

    if (!hasPermission) {
        return null;
    }

    // Adjust icon size and stroke for icons that appear visually smaller
    const iconProps = ["building2", "circlePlus"].includes(iconName) 
        ? { className: "h-9 w-9 text-primary", strokeWidth: 1.5 }
        : { className: "h-8 w-8 text-primary", strokeWidth: 2 };

    const containerSize = ["building2", "circlePlus"].includes(iconName) ? "h-9 w-9" : "h-8 w-8";

    return (
        <Link href={href} className="block group">
            <Card className={cn(
                "h-32 transition-all hover:shadow-md hover:scale-105 border hover:border-primary/20 cursor-pointer",
                className
            )}>
                <CardContent className="p-6 h-full flex flex-col items-center justify-center text-center space-y-3">
                    <div className={cn(containerSize, "flex items-center justify-center")}>
                        <Icon {...iconProps} />
                    </div>
                    <div className="space-y-1">
                        <h3 className="font-semibold text-sm">{title}</h3>
                        <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}